import { ApolloError } from '@apollo/client';
import { AnimatePresence, motion } from 'framer-motion';
import type { GraphQLFormattedError } from 'graphql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { TranscriptionLanguage } from '@treams/treams-network';
import {
	Button,
	FlexGap,
	GraphQLErrorMessage,
	Paragraph,
	PlaybackStopIcon,
	Spinner,
	tokens,
} from '@treams/treams-ui';

import { TranscriptionLanguageSelect } from './TranscriptionLanguageSelect';
import { RecordingState } from './TranscriptionRecordingContext';
import VoiceMemoWaveForm from './VoiceMemoWaveForm';

export const RecordIcon = styled.div`
	width: 14px;
	height: 14px;
	background-color: ${tokens.error.icon};
	border-radius: 50%;
	margin-left: -1px;
`;

export const BAR_STEP = 6;

export const BannerWrapper = styled(motion.div)`
	background: ${tokens.brand.background};
	border-bottom: 1px solid ${tokens.brand.border};
	position: sticky;
	top: 0;
	z-index: 1000;
	display: flex;
	justify-content: center;
`;

export const BannerLabel = styled.span`
	color: ${tokens.brand.text};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.label}px;
	font-weight: ${tokens.typography.weights.medium};
	white-space: nowrap;
`;

export const BannerLink = styled(Link)`
	color: ${tokens.brand.text};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.label}px;
	font-weight: ${tokens.typography.weights.medium};
	white-space: nowrap;
	text-decoration: none;

	&:hover {
		text-decoration: underline;
	}
`;

export const Elapsed = styled.span`
	color: ${tokens.text.secondary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.label}px;
	font-weight: ${tokens.typography.weights.medium};
	white-space: nowrap;
`;

const UploadErrorFallback = styled(Paragraph)`
	color: ${tokens.error.text};
	background: ${tokens.error.background};
	padding: ${tokens.padding.compact}px;
	border-radius: ${tokens.radius.xs}px;
	margin: 0;
`;

function recordingUploadGraphQLError(error: Error): GraphQLFormattedError | undefined {
	if (error instanceof ApolloError && error.graphQLErrors[0]) {
		return error.graphQLErrors[0];
	}
	return undefined;
}

function isRenderableRecordingGraphQLError(error: GraphQLFormattedError): boolean {
	return Boolean(error.extensions && error.extensions.error);
}

function RecordingUploadErrorBanner({ error }: { error: Error }) {
	const { t } = useTranslation();
	const gqlError = recordingUploadGraphQLError(error);
	if (gqlError && isRenderableRecordingGraphQLError(gqlError)) {
		return <GraphQLErrorMessage error={gqlError} />;
	}
	return <UploadErrorFallback small>{t('transcription.uploadFailed')}</UploadErrorFallback>;
}

export const PulsingDot = styled.div`
	width: 10px;
	height: 10px;
	background-color: ${tokens.error.icon};
	border-radius: 50%;
	animation: recording-pulse 1.5s ease-in-out infinite;

	@keyframes recording-pulse {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.4;
		}
	}
`;

export function formatElapsed(elapsed: number) {
	const minutes = Math.floor(elapsed / 60);
	const seconds = elapsed % 60;
	return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export type RecordConversationProps = {
	label?: string;
	targetPath?: string;
	state: RecordingState;
	language: TranscriptionLanguage;
	onLanguageChange: (language: TranscriptionLanguage) => void;
	countdown: number | null;
	uploadError: Error | null;
	/** When set, show retry / save actions for a failed S3 upload while the blob is still in memory. */
	canRetryUpload?: boolean;
	retryUploadLoading?: boolean;
	onRetryUpload?: () => void;
	onSaveRecordingLocally?: () => void;
	elapsed: number;
	bars: number[];
	current: number;
	onStop: () => void;
	onCancel: () => void;
	onDismissError: () => void;
};

export function RecordConversation({
	label,
	targetPath,
	state,
	language,
	onLanguageChange,
	countdown,
	uploadError,
	canRetryUpload = false,
	retryUploadLoading = false,
	onRetryUpload,
	onSaveRecordingLocally,
	elapsed,
	bars,
	current,
	onStop,
	onCancel,
	onDismissError,
}: RecordConversationProps) {
	const { t } = useTranslation();
	const [confirmingCancel, setConfirmingCancel] = useState(false);
	const [confirmingStop, setConfirmingStop] = useState(false);
	const isRecording = state === RecordingState.RECORDING;
	const busy = state === RecordingState.UPLOADING || state === RecordingState.PROCESSING;
	const visible = state !== RecordingState.IDLE || !!uploadError;

	return (
		<AnimatePresence>
			{visible && (
				<BannerWrapper
					key="recording-banner"
					initial={{ height: 0, opacity: 0 }}
					animate={{ height: 'auto', opacity: 1 }}
					exit={{ height: 0, opacity: 0 }}
					transition={{ duration: 0.2 }}
					style={{ overflow: 'hidden' }}
				>
					<FlexGap
						gap={16}
						alignItems={['flex-start', 'flex-start', 'flex-start', 'center']}
						justifyContent="space-between"
						maxWidth="1000px"
						flexGrow={1}
						flexDirection={['column', 'column', 'column', 'row']}
						paddingY="10px"
						paddingX="24px"
					>
						<FlexGap gap={12} alignItems="center">
							{countdown !== null && (
								<BannerLabel>
									{t('transcription.startingIn')} {countdown}...
								</BannerLabel>
							)}

							{isRecording && (
								<>
									<PulsingDot />
									{targetPath ? (
										<BannerLink to={targetPath}>
											{label ?? t('transcription.recordConversation')}
										</BannerLink>
									) : (
										<BannerLabel>{label ?? t('transcription.recordConversation')}</BannerLabel>
									)}
									<Elapsed>&middot; {formatElapsed(elapsed)}</Elapsed>
									<Box
										width="120px"
										height="20px"
										style={{ overflow: 'hidden', position: 'relative' }}
									>
										<motion.div
											animate={{ x: -(bars.length * BAR_STEP) }}
											transition={{ duration: 1, ease: 'linear' }}
											style={{
												position: 'absolute',
												left: `calc(100% - ${BAR_STEP}px)`,
												top: '50%',
												translateY: '-50%',
											}}
										>
											<VoiceMemoWaveForm recording bars={bars} current={current} />
										</motion.div>
									</Box>
								</>
							)}

							{busy && (
								<FlexGap gap={8} alignItems="center">
									<Spinner />
									{targetPath ? (
										<BannerLink to={targetPath}>{t('transcription.processing')}</BannerLink>
									) : (
										<BannerLabel>{t('transcription.processing')}</BannerLabel>
									)}
								</FlexGap>
							)}

							{uploadError && state === RecordingState.IDLE && (
								<RecordingUploadErrorBanner error={uploadError} />
							)}
						</FlexGap>

						<FlexGap gap={8} alignItems="center">
							{confirmingStop && (
								<FlexGap
									gap={8}
									alignItems={['flex-start', 'flex-start', 'center']}
									flexDirection={['column', 'column', 'row']}
								>
									<Paragraph small style={{ whiteSpace: 'nowrap' }}>
										{t('transcription.confirmStop')}
									</Paragraph>
									<FlexGap gap={8} alignItems="center">
										<Button
											title={t('transcription.confirmStopYes')}
											type="ai"
											small
											onClick={() => {
												setConfirmingStop(false);
												onStop();
											}}
										/>
										<Button
											title={t('transcription.confirmStopNo')}
											type="transparent"
											small
											onClick={() => setConfirmingStop(false)}
										/>
									</FlexGap>
								</FlexGap>
							)}
							{confirmingCancel && (
								<FlexGap
									gap={8}
									alignItems={['flex-start', 'flex-start', 'center']}
									flexDirection={['column', 'column', 'row']}
								>
									<Paragraph small style={{ whiteSpace: 'nowrap' }}>
										{t('transcription.confirmCancel')}
									</Paragraph>
									<FlexGap gap={8} alignItems="center">
										<Button
											title={t('transcription.confirmCancelYes')}
											type="destructive"
											small
											onClick={() => {
												setConfirmingCancel(false);
												onCancel();
											}}
										/>
										<Button
											title={t('transcription.confirmCancelNo')}
											type="transparent"
											small
											onClick={() => setConfirmingCancel(false)}
										/>
									</FlexGap>
								</FlexGap>
							)}
							{!confirmingStop && !confirmingCancel && (
								<>
									{state !== RecordingState.PROCESSING &&
										state !== RecordingState.UPLOADING &&
										!uploadError && (
											<Box width="70px">
												<TranscriptionLanguageSelect
													compact
													small
													value={language}
													onChange={onLanguageChange}
												/>
											</Box>
										)}
									{isRecording && (
										<Button
											title={t('transcription.stopRecording')}
											type="ai"
											small
											icon={<PlaybackStopIcon />}
											onClick={() => setConfirmingStop(true)}
										/>
									)}
									{(isRecording || state === RecordingState.COUNTDOWN) && (
										<Button
											title={t('common.cancel')}
											type="transparent"
											small
											onClick={() => (isRecording ? setConfirmingCancel(true) : onCancel())}
										/>
									)}
									{uploadError && state === RecordingState.IDLE && canRetryUpload && (
										<>
											<Button
												title={t('transcription.retryUpload')}
												type="ai"
												small
												disabled={retryUploadLoading}
												onClick={() => onRetryUpload?.()}
											/>
											<Button
												title={t('transcription.saveRecordingLocally')}
												type="transparent"
												small
												disabled={retryUploadLoading}
												onClick={() => onSaveRecordingLocally?.()}
											/>
										</>
									)}
									{uploadError && state === RecordingState.IDLE && (
										<Button
											title={t('common.dismiss')}
											type="transparent"
											small
											disabled={retryUploadLoading}
											onClick={onDismissError}
										/>
									)}
								</>
							)}
						</FlexGap>
					</FlexGap>
				</BannerWrapper>
			)}
		</AnimatePresence>
	);
}
